// Preisgruppen und Preislisten. Eine Preisgruppe sagt, WER welchen Preis bekommt (Filialpreis, Gastronomie …) und ob
// ihre Belege brutto oder netto rechnen; Kunden hängen über ihre Preisgruppe daran (Stammdaten). Eine Preisliste
// trägt die Preise: für eine Preisgruppe, wahlweise nur für einen Kunden (Sonderpreise) oder nur an einem Standort.
import { defineContract } from '@shared/contract'
import { zId } from '@shared/ids'
import { listInput, listOutput } from '@shared/list'
import { z } from 'zod'
import { zById, zDay, zExpectedVersion, zLocationRef, zOptionalText, zPriceMode, zSaved, zText } from './common'

const READ = 'pricing.price.read'
const WRITE = 'pricing.price.write'

// ---- Preisgruppen --------------------------------------------------------------------------------------

export const zPriceGroupRow = z.object({
  id: zId,
  key: z.string(),
  name: z.string(),
  priceMode: zPriceMode,
  isDefault: z.boolean(),
  active: z.boolean(),
  version: z.int(),
  /** aktive Kunden mit dieser Preisgruppe */
  customerCount: z.int(),
  /** aktive Preislisten der Gruppe (ohne Aktionen) */
  listCount: z.int(),
})

export const priceGroupList = defineContract({
  name: 'pricing.priceGroup.list',
  title: 'Preisgruppen anzeigen',
  description: 'Listet die Preisgruppen des Betriebs mit Basis (brutto/netto) und der Zahl ihrer Kunden.',
  llm: {
    description:
      'Listet alle Preisgruppen (Ladenpreis, Gewerbe, Handel …) mit brutto/netto, Zahl der zugeordneten Kunden und ' +
      'Preislisten. Verwenden, um eine Preisgruppe zu finden, bevor Preise gelesen oder gesetzt werden. Welche Kunden ' +
      'zu einer Gruppe gehören: masterdata.customer.list mit priceGroupId.',
    keywords: ['Preisgruppe', 'Kundengruppe', 'Filialpreis', 'brutto', 'netto'],
  },
  input: z.strictObject({ active: z.boolean().optional() }),
  output: z.object({ rows: z.array(zPriceGroupRow) }),
  permission: READ,
  risk: 'read',
})

export const priceGroupCreate = defineContract({
  name: 'pricing.priceGroup.create',
  title: 'Preisgruppe anlegen',
  description: 'Legt eine Preisgruppe an. Kunden bekommen sie in den Stammdaten zugewiesen.',
  llm: {
    description:
      'Legt eine neue Preisgruppe an (Schlüssel in Kleinbuchstaben, Name, brutto oder netto). Die Gruppe hat danach ' +
      'noch keine Preise – dafür pricing.priceList.create. Nicht für Aktionen (pricing.promotion.create).',
    keywords: ['Preisgruppe anlegen', 'neue Kundengruppe'],
  },
  input: z.strictObject({
    key: z
      .string({ error: 'Bitte einen Schlüssel eingeben.' })
      .trim()
      .regex(/^[a-z][a-z0-9_]{1,39}$/, { error: 'Der Schlüssel besteht aus Kleinbuchstaben, Ziffern und Unterstrich (2 bis 40 Zeichen).' }),
    name: zText(80),
    priceMode: zPriceMode,
  }),
  output: zSaved,
  permission: WRITE,
  risk: 'write',
})

export const priceGroupUpdate = defineContract({
  name: 'pricing.priceGroup.update',
  title: 'Preisgruppe ändern',
  description: 'Ändert Name oder Basis einer Preisgruppe. Brutto/netto ist fest, sobald die Gruppe Preislisten hat.',
  llm: {
    description:
      'Ändert Name oder brutto/netto einer Preisgruppe – nur die mitgegebenen Felder. Brutto/netto lässt sich nicht ' +
      'mehr ändern, sobald Preislisten der Gruppe existieren.',
    keywords: ['Preisgruppe umbenennen', 'Preisgruppe ändern'],
  },
  input: z.strictObject({ id: zId, expectedVersion: zExpectedVersion, name: zText(80).optional(), priceMode: zPriceMode.optional() }),
  output: zSaved,
  permission: WRITE,
  risk: 'write',
})

export const priceGroupDeactivate = defineContract({
  name: 'pricing.priceGroup.deactivate',
  title: 'Preisgruppe stilllegen',
  description: 'Nimmt eine Preisgruppe aus der Auswahl. Geht nur, wenn ihr kein aktiver Kunde mehr zugeordnet ist.',
  llm: {
    description:
      'Legt eine Preisgruppe still: Sie steht nicht mehr zur Auswahl und die Preisfindung nutzt sie nicht mehr. ' +
      'Nicht möglich für die Standard-Preisgruppe und solange aktive Kunden zugeordnet sind.',
    keywords: ['Preisgruppe stilllegen', 'Preisgruppe löschen', 'deaktivieren'],
  },
  input: zById,
  output: zSaved,
  permission: WRITE,
  risk: 'destructive',
  effects: { reversible: true },
})

export const priceGroupReactivate = defineContract({
  name: 'pricing.priceGroup.reactivate',
  title: 'Preisgruppe wieder aktivieren',
  description: 'Nimmt eine stillgelegte Preisgruppe wieder in die Auswahl.',
  llm: { description: 'Aktiviert eine stillgelegte Preisgruppe wieder.', keywords: ['Preisgruppe aktivieren'] },
  input: zById,
  output: zSaved,
  permission: WRITE,
  risk: 'write',
})

// ---- Preislisten ---------------------------------------------------------------------------------------

/** base = Liste der Standard-Preisgruppe · group = Liste einer Kundengruppe · customer = Sonderpreise eines Kunden · promotion = Aktion */
export const PRICE_LIST_KINDS = ['base', 'group', 'customer', 'promotion'] as const
export type PriceListKind = (typeof PRICE_LIST_KINDS)[number]

/** active = gilt heute · planned = beginnt später · expired = Zeitraum vorbei · inactive = stillgelegt bzw. nicht freigegeben */
export const PRICE_LIST_STATES = ['active', 'planned', 'expired', 'inactive'] as const
export type PriceListState = (typeof PRICE_LIST_STATES)[number]

export const zPriceListRow = z.object({
  id: zId,
  name: z.string(),
  kind: z.enum(PRICE_LIST_KINDS),
  state: z.enum(PRICE_LIST_STATES),
  priceMode: zPriceMode,
  priceGroup: z.object({ id: zId, name: z.string() }).nullable(),
  partner: z.object({ id: zId, number: z.string(), name: z.string() }).nullable(),
  /** gilt nur an diesem Standort; null = überall */
  location: zLocationRef.nullable(),
  validFrom: z.string().nullable(),
  validTo: z.string().nullable(),
  priority: z.int(),
  active: z.boolean(),
  /** Gebinde mit einem heute oder künftig gültigen Preis */
  priceCount: z.int(),
  version: z.int(),
})
export type PriceListRow = z.output<typeof zPriceListRow>

export const priceListList = defineContract({
  name: 'pricing.priceList.list',
  title: 'Preislisten anzeigen',
  description: 'Listet die Preislisten: je Preisgruppe, Kundensonderpreise, Standortpreise und Aktionslisten.',
  llm: {
    description:
      'Listet Preislisten mit Art (Basis, Kundengruppe, Kundensonderpreis, Aktion), brutto/netto, Gültigkeit, Standort ' +
      'und Zahl der Preise. Filter: kind, priceGroupId, locationId, active. Die Preise einer Liste: ' +
      'pricing.priceList.prices. Was ein bestimmter Kunde zahlt: pricing.price.quote.',
    keywords: ['Preisliste', 'Preislisten', 'Sonderpreise', 'Standortpreis', 'Konditionen'],
  },
  input: listInput({
    sortBy: ['name', 'validFrom'],
    filters: {
      kind: z.enum(PRICE_LIST_KINDS).optional(),
      priceGroupId: zId.optional(),
      locationId: zId.optional(),
      partnerId: zId.optional(),
      active: z.boolean().optional(),
    },
  }),
  output: listOutput(zPriceListRow),
  permission: READ,
  risk: 'read',
})

export const priceListGet = defineContract({
  name: 'pricing.priceList.get',
  title: 'Preisliste öffnen',
  description: 'Zeigt Kopf und Kennzahlen einer Preisliste.',
  llm: {
    description:
      'Liefert den Kopf einer Preisliste: Art, Preisgruppe, Kunde, Standort, Gültigkeit, brutto/netto, Zahl der Preise ' +
      'und – bei Aktionslisten – die zugehörige Aktion (`promotionId`). Die Preise selbst: pricing.priceList.prices.',
    keywords: ['Preisliste öffnen'],
  },
  input: zById,
  output: zPriceListRow.extend({
    /** Aktionsliste: die Aktion, aus der sie entstanden ist */
    promotionId: zId.nullable(),
    /** vorbereitete Massenänderungen dieser Liste, die auf Freigabe warten */
    pendingChanges: z.int(),
    /** Preise, die erst künftig gelten */
    futurePriceCount: z.int(),
  }),
  permission: READ,
  risk: 'read',
})

export const priceListCreate = defineContract({
  name: 'pricing.priceList.create',
  title: 'Preisliste anlegen',
  description: 'Legt eine Preisliste für eine Preisgruppe an – wahlweise nur für einen Kunden oder einen Standort.',
  llm: {
    description:
      'Legt eine leere Preisliste an. `priceGroupId` bindet sie an eine Preisgruppe (ohne Angabe: Basisliste für alle), ' +
      '`partnerId` macht sie zur Sonderpreisliste eines Kunden, `locationId` beschränkt sie auf einen Standort. ' +
      '`priceMode` nur angeben, wenn die Liste von der Preisgruppe abweichen soll. Preise danach mit pricing.price.set. ' +
      'Nicht für zeitlich begrenzte Aktionen – dafür pricing.promotion.create bzw. pricing.promotion.propose.',
    keywords: ['Preisliste anlegen', 'Sonderpreisliste', 'Kundenpreise', 'Standortpreise'],
  },
  input: z.strictObject({
    name: zText(120),
    priceGroupId: zId.nullish(),
    partnerId: zId.nullish(),
    locationId: zId.nullish(),
    priceMode: zPriceMode.optional(),
    validFrom: zDay.nullish(),
    validTo: zDay.nullish(),
    priority: z.int().min(-100).max(100).default(0),
  }),
  output: zSaved,
  permission: WRITE,
  risk: 'write',
})

export const priceListUpdate = defineContract({
  name: 'pricing.priceList.update',
  title: 'Preisliste ändern',
  description: 'Ändert Name, Gültigkeit oder Rang einer Preisliste. Preisgruppe, Kunde und Standort bleiben, wie sie sind.',
  llm: {
    description:
      'Ändert Name, Gültigkeitszeitraum (`validFrom`, `validTo`; null leert) oder Rang (`priority`: bei mehreren ' +
      'passenden Listen gewinnt die höhere Zahl). Preisgruppe, Kunde, Standort und brutto/netto lassen sich nicht ' +
      'nachträglich ändern – dafür eine neue Liste anlegen. Aktionslisten ändert man über die Aktion.',
    keywords: ['Preisliste umbenennen', 'Gültigkeit Preisliste', 'Preisliste verlängern'],
  },
  input: z.strictObject({
    id: zId,
    expectedVersion: zExpectedVersion,
    name: zText(120).optional(),
    validFrom: zDay.nullish(),
    validTo: zDay.nullish(),
    priority: z.int().min(-100).max(100).optional(),
    note: zOptionalText(300),
  }),
  output: zSaved,
  permission: WRITE,
  risk: 'financial',
})

export const priceListDeactivate = defineContract({
  name: 'pricing.priceList.deactivate',
  title: 'Preisliste stilllegen',
  description: 'Nimmt eine Preisliste aus der Preisfindung. Ihre Preise bleiben für den Verlauf erhalten.',
  llm: {
    description:
      'Legt eine Preisliste still: Die Preisfindung berücksichtigt sie ab sofort nicht mehr; gelöscht wird nichts. ' +
      'Die Vorschau nennt, wie viele Gebinde dadurch ihren Preis in dieser Liste verlieren. Die Basisliste der ' +
      'Standard-Preisgruppe lässt sich nicht stilllegen. Eine laufende Aktion beendet man mit pricing.promotion.end.',
    keywords: ['Preisliste stilllegen', 'Preisliste deaktivieren', 'Preisliste löschen'],
  },
  input: zById,
  output: zSaved,
  permission: WRITE,
  risk: 'destructive',
  effects: { reversible: true },
})

export const priceListReactivate = defineContract({
  name: 'pricing.priceList.reactivate',
  title: 'Preisliste wieder aktivieren',
  description: 'Nimmt eine stillgelegte Preisliste wieder in die Preisfindung.',
  llm: {
    description: 'Aktiviert eine stillgelegte Preisliste wieder. Nicht für Aktionslisten, die nie freigegeben wurden.',
    keywords: ['Preisliste aktivieren'],
  },
  input: zById,
  output: zSaved,
  permission: WRITE,
  risk: 'financial',
})
